// T071: Create image upload component with progress
// T072: Handle image upload errors and retries

import { useState, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { uploadImage, validateImageFile, type UploadProgress } from '@/services/imageUpload'
import { useAuthStore } from '@/stores/authStore'
import { X, Upload, Image as ImageIcon, Loader2 } from 'lucide-react'

interface ImageUploadProps {
  onImageUploaded: (url: string) => void
  onRemove?: (url: string) => void
  uploadedImages?: string[]
  maxImages?: number
}

export function ImageUpload({
  onImageUploaded,
  onRemove,
  uploadedImages = [],
  maxImages = 5,
}: ImageUploadProps) {
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState<UploadProgress | null>(null)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { user } = useAuthStore()

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Check if user is authenticated
    if (!user) {
      setError('Please login to upload images')
      return
    }

    // Reset error
    setError(null)

    // Validate file
    const validation = validateImageFile(file)
    if (!validation.valid) {
      setError(validation.error || 'Invalid file')
      return
    }

    // Check max images limit
    if (uploadedImages.length >= maxImages) {
      setError(`Maximum ${maxImages} images allowed`)
      return
    }

    // Upload
    setUploading(true)
    setProgress(null)

    try {
      const result = await uploadImage({
        file,
        username: user.username,
        postingKey: user.postingKey,
        onProgress: (prog) => {
          setProgress(prog)
        },
      })

      if (result.success && result.url) {
        onImageUploaded(result.url)
        setProgress(null)
        // Reset input
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      } else {
        setError(result.error || 'Upload failed')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const handleRetry = () => {
    setError(null)
    fileInputRef.current?.click()
  }

  return (
    <div className="space-y-4">
      {/* Upload Button */}
      <div className="flex items-center gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
          onChange={handleFileSelect}
          disabled={uploading || uploadedImages.length >= maxImages}
          className="hidden"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || uploadedImages.length >= maxImages}
        >
          {uploading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="mr-2 h-4 w-4" />
              Add Image
            </>
          )}
        </Button>
        {uploadedImages.length > 0 && (
          <span className="text-sm text-muted-foreground">
            {uploadedImages.length} / {maxImages}
          </span>
        )}
      </div>

      {/* Progress Bar */}
      {progress && (
        <div className="space-y-1">
          <div className="flex justify-between text-sm">
            <span>Uploading...</span>
            <span>{progress.percentage}%</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${progress.percentage}%` }}
            />
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="flex items-center justify-between rounded-lg border border-destructive bg-destructive/10 p-3 text-sm">
          <span className="text-destructive">{error}</span>
          <Button type="button" variant="ghost" size="sm" onClick={handleRetry}>
            Retry
          </Button>
        </div>
      )}

      {/* Uploaded Images Preview */}
      {uploadedImages.length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {uploadedImages.map((url) => (
            <div key={url} className="group relative aspect-square overflow-hidden rounded-lg border">
              <img
                src={url}
                alt="Uploaded"
                className="h-full w-full object-cover"
              />
              {onRemove && (
                <button
                  type="button"
                  onClick={() => onRemove(url)}
                  className="absolute right-2 top-2 rounded-full bg-black/60 p-1 text-white opacity-0 transition-opacity hover:bg-black/80 group-hover:opacity-100"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Help Text */}
      {uploadedImages.length === 0 && !error && !uploading && (
        <p className="text-sm text-muted-foreground">
          <ImageIcon className="mr-1 inline h-4 w-4" />
          JPEG, PNG, GIF, or WebP. Max 10MB per image.
        </p>
      )}
    </div>
  )
}
